/**
 * Pipeline Message Log Types
 * Types for the msglog audit/debugging tool
 */

// Search type options for the pipeline logs endpoint
export type PipelineSearchType = 'eventID' | 'clientID' | 'assetID' | 'encoderID' | 'freetext';

// Log severity levels
export type PipelineLogType = 'critical' | 'error' | 'warning' | 'info' | 'debug';

// Category for grouping messages
export type PipelineCategory = 'encoder' | 'transcode' | 'events' | 'egress' | 'asset' | 'infrastructure' | 'storage' | 'email' | 'other';

/**
 * Query parameters for GET /api/pipeline/logs
 */
export interface PipelineLogQueryParams {
  // Tier 1: Core search (covers 70% of debugging)
  searchType: PipelineSearchType;
  searchValue: string;
  dateFrom: string;              // ISO date (required, max 3 month window)
  dateTo?: string;               // ISO date (defaults to now)
  logType?: PipelineLogType;     // Filter by severity

  // Tier 2: Extended search
  category?: PipelineCategory;

  // Pagination
  limit?: number;                // default 500, max 2000
  offset?: number;
}

/**
 * Extracted fields from message content
 * Parsed from JSON or regex patterns on plain text
 */
export interface PipelineLogExtracted {
  category?: PipelineCategory;
  action?: string;               // connect, disconnect, start, stop, processing, etc.

  // Common IDs (extracted from JSON or regex on plain text)
  eventID?: string;
  clientID?: string;
  assetID?: string;
  encoderID?: string;
  encoderName?: string;
  instanceID?: string;
  transcodeID?: string;
  streamName?: string;
  streamID?: string;
  ipAddress?: string;
  channelID?: string;

  // Context
  progress?: number;             // for transcode processing messages
  hasError?: boolean;            // from JSON errors.hasError or inferred from log type

  // PHP/Backend error details
  errorFile?: string;            // file path from PHP errors
  errorLine?: number;            // line number from PHP errors
  errorClass?: string;           // class name from Class Not Found errors
  errorMessage?: string;         // extracted error message
}

/**
 * Individual log entry response shape
 * Uniform structure for all log entries regardless of source format
 */
export interface PipelineLogEntry {
  id: string;
  logDate: string;
  type: PipelineLogType;
  rawMessage: string;
  isTruncated: boolean;          // true if JSON was cut off at VARCHAR(1000) limit

  extracted: PipelineLogExtracted;
}

/**
 * Response from GET /api/pipeline/logs
 */
export interface PipelineLogsResponse {
  logs: PipelineLogEntry[];
  meta: {
    hasMore: boolean;  // true if more results available (replaces total count for performance)
    limit: number;
    offset: number;
    dateFrom: string;
    dateTo: string;
  };
}

/**
 * Constants for pipeline log configuration
 */
export const PIPELINE_CONFIG = {
  // Maximum time window for queries (2191 hours = ~3 months)
  MAX_WINDOW_HOURS: 2191,
  // Default result limit (reduced from 500 for faster queries)
  DEFAULT_LIMIT: 50,
  // Maximum result limit (reduced from 2000 for faster queries)
  MAX_LIMIT: 200,
  // Log type priority order (for sorting)
  LOG_TYPE_PRIORITY: {
    critical: 0,
    error: 1,
    warning: 2,
    info: 3,
    debug: 4,
  } as const,
} as const;
